import { useEffect, useState } from 'react';
import { BarChart3, Mail, MousePointer, Eye, UserMinus, Send, AlertCircle } from 'lucide-react';
import { V1Layout } from '../../components/v1/V1Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../ui/table';
import { Badge } from '../../../../ui/badge';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { format, subDays } from 'date-fns';
import type { MessageType } from '../../../../../types/epicengage';

interface EmailStats {
  total_sent: number;
  total_delivered: number;
  total_opened: number;
  total_clicked: number;
  total_unsubscribed: number;
}

interface MessageStats {
  id: string;
  name: string;
  type: MessageType;
  status: 'SENT' | 'FAILED';
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  unsubscribed_count: number;
  sent_at: string | null;
}

export default function Analytics() {
  const { merchant } = useMerchant();
  const [loading, setLoading] = useState(true);
  const [overallStats, setOverallStats] = useState<EmailStats>({
    total_sent: 0,
    total_delivered: 0,
    total_opened: 0,
    total_clicked: 0,
    total_unsubscribed: 0
  });
  const [messageStats, setMessageStats] = useState<MessageStats[]>([]);

  useEffect(() => {
    if (merchant) {
      fetchStats();
    }
  }, [merchant]);

  const fetchStats = async () => {
    if (!merchant) return;
    setLoading(true);

    // Fetch ONLY SENT or FAILED messages from last 30 days
    // NO SCHEDULED messages should appear in analytics
    const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
    
    const { data: messages, error } = await epicengageSupabase
      .from('scheduled_messages')
      .select(`
        *,
        email_templates_v2 (name)
      `)
      .eq('merchant_id', merchant.id)
      .in('status', ['SENT', 'FAILED']) // Only show completed sends
      .gte('sent_at', thirtyDaysAgo)
      .order('sent_at', { ascending: false });

    if (error) {
      console.error('Error fetching messages:', error);
      setLoading(false);
      return;
    }

    // Also fetch manual blasts that are SENT only
    const { data: blasts } = await epicengageSupabase
      .from('manual_blasts')
      .select(`
        *,
        email_templates_v2 (name)
      `)
      .eq('merchant_id', merchant.id)
      .eq('status', 'SENT') // Only SENT blasts
      .gte('created_at', thirtyDaysAgo)
      .order('created_at', { ascending: false });

    // Calculate overall stats - only count SENT messages
    const allMessages = messages || [];
    const sentMessages = allMessages.filter(m => m.status === 'SENT');
    const sentBlasts = blasts || [];
    
    const totalSent = sentMessages.length + sentBlasts.reduce((acc, b) => acc + b.sent_count, 0);
    const totalDelivered = totalSent;

    // Get total unsubscribes for this merchant
    const { count: totalUnsubs } = await epicengageSupabase
      .from('customer_suppressions')
      .select('*', { count: 'exact', head: true })
      .eq('merchant_id', merchant.id)
      .eq('channel', 'EMAIL');
    
    setOverallStats({
      total_sent: totalSent,
      total_delivered: totalDelivered,
      total_opened: 0,
      total_clicked: 0,
      total_unsubscribed: totalUnsubs || 0
    });

    // Group scheduled_messages by template and date for campaign-level view
    const campaignMap = new Map<string, MessageStats>();
    
    allMessages.forEach(msg => {
      const templateId = msg.template_id || 'unknown';
      const templateName = (msg.email_templates_v2 as any)?.name || msg.subject_snapshot || 'Unknown';
      const sentDate = msg.sent_at ? format(new Date(msg.sent_at), 'yyyy-MM-dd') : 'unknown';
      const campaignKey = `${msg.type}-${templateId}-${sentDate}`;
      
      const existing = campaignMap.get(campaignKey);
      if (existing) {
        // Aggregate counts
        existing.sent_count += msg.status === 'SENT' ? 1 : 0;
        existing.delivered_count += msg.status === 'SENT' ? 1 : 0;
        // Keep the earliest sent_at for the campaign
        if (msg.sent_at && (!existing.sent_at || new Date(msg.sent_at) < new Date(existing.sent_at))) {
          existing.sent_at = msg.sent_at;
        }
        // If any message failed, mark as partial failure
        if (msg.status === 'FAILED') {
          existing.status = 'SENT'; // Still show as sent if some succeeded
        }
      } else {
        campaignMap.set(campaignKey, {
          id: campaignKey,
          name: templateName,
          type: msg.type as MessageType,
          status: msg.status as 'SENT' | 'FAILED',
          sent_count: msg.status === 'SENT' ? 1 : 0,
          delivered_count: msg.status === 'SENT' ? 1 : 0,
          opened_count: 0,
          clicked_count: 0,
          unsubscribed_count: 0,
          sent_at: msg.sent_at
        });
      }
    });

    // Convert map to array
    const messageRows: MessageStats[] = Array.from(campaignMap.values());

    // Add manual blasts (already aggregated)
    sentBlasts.forEach(blast => {
      const templateName = (blast.email_templates_v2 as any)?.name || 'Manual Blast';
      messageRows.push({
        id: blast.id,
        name: templateName,
        type: 'MANUAL_BLAST' as MessageType,
        status: 'SENT',
        sent_count: blast.sent_count,
        delivered_count: blast.sent_count,
        opened_count: 0,
        clicked_count: 0,
        unsubscribed_count: 0,
        sent_at: blast.scheduled_at
      });
    });

    // Sort by sent_at descending
    setMessageStats(messageRows.sort((a, b) => {
      const aDate = a.sent_at || '';
      const bDate = b.sent_at || '';
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    }));
    setLoading(false);
  };

  const getTypeLabel = (type: MessageType) => {
    switch (type) {
      case 'AUTOMATED': return { label: 'Automation', variant: 'secondary' as const };
      case 'EVENT_SCHEDULED': return { label: 'Event', variant: 'outline' as const };
      case 'MANUAL_BLAST': return { label: 'Manual', variant: 'default' as const };
      default: return { label: type, variant: 'secondary' as const };
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SENT': return { label: 'Sent', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'FAILED': return { label: 'Failed', variant: 'destructive' as const, className: 'bg-red-100 text-red-800' };
      default: return { label: status, variant: 'secondary' as const, className: '' };
    }
  };

  const formatTimestamp = (msg: MessageStats) => {
    if (msg.sent_at) {
      return format(new Date(msg.sent_at), 'MMM d, h:mm a');
    }
    return '-';
  };

  const calcPercent = (value: number, total: number) => {
    if (total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  return (
    <V1Layout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="h-8 w-8" />
            Analytics
          </h1>
          <p className="text-muted-foreground mt-1">
            Email performance metrics for the last 30 days
          </p>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading analytics...</div>
        ) : (
          <>
            {/* Overall Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <Send className="h-4 w-4" />
                    <span className="text-sm">Sent</span>
                  </div>
                  <p className="text-3xl font-bold">{overallStats.total_sent}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <Mail className="h-4 w-4" />
                    <span className="text-sm">Delivered</span>
                  </div>
                  <p className="text-3xl font-bold">{overallStats.total_delivered}</p>
                  <p className="text-sm text-muted-foreground">
                    {calcPercent(overallStats.total_delivered, overallStats.total_sent)}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <Eye className="h-4 w-4" />
                    <span className="text-sm">Opened</span>
                  </div>
                  <p className="text-3xl font-bold">{overallStats.total_opened}</p>
                  <p className="text-sm text-muted-foreground">
                    {calcPercent(overallStats.total_opened, overallStats.total_delivered)}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <MousePointer className="h-4 w-4" />
                    <span className="text-sm">Clicked</span>
                  </div>
                  <p className="text-3xl font-bold">{overallStats.total_clicked}</p>
                  <p className="text-sm text-muted-foreground">
                    {calcPercent(overallStats.total_clicked, overallStats.total_opened)}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <UserMinus className="h-4 w-4" />
                    <span className="text-sm">Unsubscribed</span>
                  </div>
                  <p className="text-3xl font-bold">{overallStats.total_unsubscribed}</p>
                  <p className="text-sm text-muted-foreground">
                    {calcPercent(overallStats.total_unsubscribed, overallStats.total_sent)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Notice about tracking */}
            {overallStats.total_opened === 0 && overallStats.total_sent > 0 && (
              <Card className="mb-6 border-orange-200 bg-orange-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-orange-800">Open & Click tracking coming soon</p>
                      <p className="text-sm text-orange-700 mt-1">
                        Currently showing send counts only. Detailed engagement metrics will be available when webhook tracking is enabled.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Per-Message Table */}
            <Card>
              <CardHeader>
                <CardTitle>Campaigns</CardTitle>
                <CardDescription>Performance breakdown by campaign</CardDescription>
              </CardHeader>
              <CardContent>
                {messageStats.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No emails sent in the last 30 days
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sent At</TableHead>
                        <TableHead className="text-right">Sent</TableHead>
                        <TableHead className="text-right">Delivered</TableHead>
                        <TableHead className="text-right">Opened</TableHead>
                        <TableHead className="text-right">Clicked</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {messageStats.map((msg) => {
                        const typeInfo = getTypeLabel(msg.type);
                        const statusInfo = getStatusBadge(msg.status);
                        return (
                          <TableRow key={msg.id}>
                            <TableCell className="font-medium">{msg.name}</TableCell>
                            <TableCell>
                              <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusInfo.className}>{statusInfo.label}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatTimestamp(msg)}
                            </TableCell>
                            <TableCell className="text-right">{msg.sent_count}</TableCell>
                            <TableCell className="text-right">
                              {msg.sent_count > 0 ? calcPercent(msg.delivered_count, msg.sent_count) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {msg.delivered_count > 0 ? calcPercent(msg.opened_count, msg.delivered_count) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {msg.opened_count > 0 ? calcPercent(msg.clicked_count, msg.opened_count) : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </V1Layout>
  );
}
